import { Controller, useWatch } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import { useCallback, useEffect, useState } from "react";
import {
  Description,
  DropzoneContainer,
  Error,
  FormItem,
  InputLabel,
  PreviewImage,
  PreviewImageContainer,
  PreviewImageContent,
  PreviewImageContentRow,
  PreviewImageName,
  PreviewImageSize,
  RemoveButtonPreview,
  Required,
  DropLabel,
  ClickLabel,
} from "./styles";
import FileIcon from "../../icons/File";
import { useToast } from "../../hooks/toast";
import { useIntl } from "react-intl";
import { MdError } from "react-icons/md";

export const MARLABS_DEFAULT =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR1HakUW8f2Uf03gxKrnuYvMs-2L_yQKCQspw&s";

export const ControlledDropzoneSingle = ({
  name,
  control,
  multiple = false,
  setValue,
  errors,
  required,
  description,
  subtitle,
  label,
  index,
  setErrorForm,
  clearErrors,
  defaultName = undefined,
  ...rest
}) => {
  const [imageDetails, setImageDetails] = useState(null);
  const intl = useIntl();
  const watch = useWatch({ control, name });

  useEffect(() => {
    if (watch === undefined) {
      setImageDetails(null);
      return;
    }

    if (watch && !imageDetails) {
      setImageDetails(getFileDetails(watch, defaultName));

      return;
    }
  }, [defaultName, imageDetails, watch]);

  const labelIntl = intl.formatMessage({
    id: label,
  });

  const errorMessage =
    errors?.[name] &&
    intl.formatMessage({
      id:
        errors?.[name]?.message === "file-too-large-PNG/JPG/JPEG"
          ? "file-too-large-PNG/JPG/JPEG"
          : "error.field_required",
    });
  const isRequired = required && <Required>*</Required>;
  const hasDesc = description && (
    <Description>({intl.formatMessage({ id: description })})</Description>
  );
  const hasDesc2 = subtitle && (
    <Description subtitle>{intl.formatMessage({ id: subtitle })}</Description>
  );

  const handleReset = () => {
    setValue(name, null);
    setImageDetails(getFileDetails(null));
  };

  const onDrop = useCallback(
    (files) => {
      if (!files[0]) {
        return;
      }
      setImageDetails(getFileDetails(files[0]));
      setValue(name, files[0]);

      if (clearErrors) {
        clearErrors(name);
      }
    },
    [setValue, name, clearErrors]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => (
        <FormItem>
          <InputLabel subtitle={subtitle}>
            {labelIntl}
            {isRequired} {hasDesc}
          </InputLabel>
          {hasDesc2}
          <Dropzone
            multiple={multiple}
            reset={handleReset}
            onDrop={onDrop}
            error={errorMessage}
            imageDetails={imageDetails}
            name={name}
            setError={setErrorForm}
            {...rest}
          />
          {errorMessage && (
            <Error>
              <MdError color="#DC2626" size={14} />
              {errorMessage}
            </Error>
          )}
        </FormItem>
      )}
    />
  );
};

const Dropzone = ({
  multiple,
  reset,
  imageDetails,
  onChange,
  onDrop,
  accept = ACCEPT,
  maxFileSize,
  error,
  name,
  setError,
  maxSize = 1 * 1024 * 1024, // 1mb
  ...rest
}) => {
  const { addToast } = useToast();
  const intl = useIntl();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    onDrop,
    accept: [".jpg", ".jpeg", ".png"],
    maxFiles: 1,
    maxSize: 1 * 1024 * 1024,
    onDropRejected: () => {
      setError(name, {
        type: "manual",
        message: "file-too-large-PNG/JPG/JPEG",
      });
    },
    ...rest,
  });

  return (
    <DropzoneContainer
      {...getRootProps()}
      isDragActive={isDragActive}
      error={error}
    >
      {!imageDetails && (
        <>
          <div>
            <FileIcon />
            <DropLabel>
              {intl.formatMessage({
                id: "drop_file_here",
              })}
            </DropLabel>
            <ClickLabel>
              {intl.formatMessage({
                id: "upload_from_computer",
              })}
            </ClickLabel>
          </div>
          <input
            {...getInputProps({ onChange })}
            accept="image/png, image/jpeg, image/jpg"
          />
        </>
      )}

      {imageDetails && (
        <PreviewImageContainer>
          <PreviewImageContent>
            <PreviewImage
              src={imageDetails.fileURL}
              alt="Preview"
              onError={() => {
                reset();
                addToast({
                  type: "error",
                  title: intl.formatMessage({
                    id: "lesson.errorToLoadImage",
                  }),
                });
              }}
            />
            <div>
              <PreviewImageContentRow title={imageDetails.fileName}>
                <PreviewImageName>{imageDetails.fileName}</PreviewImageName>
                <PreviewImageSize>{imageDetails.fileSize} MB</PreviewImageSize>
              </PreviewImageContentRow>
              <RemoveButtonPreview onClick={reset}>Remover</RemoveButtonPreview>
            </div>
          </PreviewImageContent>
        </PreviewImageContainer>
      )}
    </DropzoneContainer>
  );
};

const ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
};

const getFileDetails = (file, defaultName) => {
  if (!file) return null;

  const fileURL = URL.createObjectURL(file);
  const fileName = file.name;
  const fileSize = file.size;
  const fileSizeInMB = (fileSize / (1024 * 1024)).toFixed(2);

  return {
    fileURL,
    fileName: defaultName ? defaultName : fileName,
    fileSize: fileSizeInMB,
  };
};